import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { FilterTemplate } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog'
import { H2 } from '@repo/ui/h2'
import { useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Label } from '@repo/ui/label'
import { Switch } from '@repo/ui/switch'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { cn } from '@repo/ui/utils'
import {
  ArrowDownUpIcon,
  EyeIcon,
  Grid2x2PlusIcon,
  GripVerticalIcon,
  Users2Icon,
} from 'lucide-react'
import { ReactNode, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'

import { Sortable } from '@/components/admin/structure-data/display/Sortable'
import { useWidgets } from '@/components/dashboard/widgets/hook'
import { selectSavedFilterTemplates } from '@/store/beneficiary-list-filters/selectors'
import { defaultWidget, setWidgets } from '@/store/dashboard-widgets/slice'
import { useStoreDispatch } from '@/store/hooks'

export function WidgetsSelector() {
  const { t, tc } = useT({ keyPrefix: 'dashboard.widgets.editor' })
  const dispatch = useStoreDispatch()
  const { savedWidgetIds, setSavedWidgetIds, reorderedWidgets } = useWidgets()
  const savedTemplates = useSelector(selectSavedFilterTemplates)

  const handleCheckboxChange = useCallback(
    (id: FilterTemplate['id']) => {
      if (savedWidgetIds.includes(id)) {
        if (savedWidgetIds.length === 1) {
          if (id === defaultWidget.filterTemplate.id) return
          // When removing the last widget, reset to default widget
          setSavedWidgetIds([defaultWidget.filterTemplate.id])
          return
        }
        setSavedWidgetIds((prev) => prev.filter((widgetId) => widgetId !== id))
      } else {
        setSavedWidgetIds((prev) => [...prev, id])
      }
    },
    [savedWidgetIds],
  )

  useEffect(() => {
    const templatesToSetAsWidgets = savedTemplates.filter((template) =>
      savedWidgetIds.includes(template.id),
    )
    dispatch(setWidgets(templatesToSetAsWidgets))
  }, [savedWidgetIds, savedTemplates])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const activeId = active.id as string
    const overId = over.id as string

    const oldIndex = savedWidgetIds.indexOf(activeId)
    const newIndex = savedWidgetIds.indexOf(overId)
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(savedWidgetIds, oldIndex, newIndex)
      setSavedWidgetIds(newOrder)
      toast.success(tc('reorderSuccess', { cfl: true }))
    }
  }

  if (savedTemplates.length === 0) return null
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Grid2x2PlusIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <H2 className="flex items-center gap-2 text-lg mb-4">
            <EyeIcon className="size-5 text-primary-600" />
            {tc('display', { cfl: true })}
          </H2>
          {savedTemplates.map((filter) => (
            <Container key={filter.id}>
              <WidgetLabel id={filter.id} name={filter.name} />

              <TooltipWrapper
                content={
                  savedWidgetIds.includes(filter.id)
                    ? tc('shown', { cfl: true })
                    : tc('hidden', { cfl: true })
                }
              >
                <Switch
                  checked={savedWidgetIds.includes(filter.id)}
                  onCheckedChange={() => handleCheckboxChange(filter.id)}
                />
              </TooltipWrapper>
            </Container>
          ))}
        </div>
        {reorderedWidgets.length >= 2 && (
          <div className="flex flex-col gap-1 mt-4">
            <H2 className="flex items-center gap-2 text-lg mb-4">
              <ArrowDownUpIcon className="size-5 text-primary-600" />
              {tc('reorder', { cfl: true })}
            </H2>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={savedTemplates.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {reorderedWidgets.map((widget, index) => (
                  <Sortable key={widget.filterTemplate.id} id={widget.filterTemplate.id} effect>
                    {({ attributes, listeners, isDragging }) => (
                      <Container
                        className={isDragging ? 'bg-primary-100 hover:bg-primary-100' : ''}
                      >
                        <InlineCode className="text-base px-3">{index + 1}</InlineCode>

                        <WidgetLabel
                          id={widget.filterTemplate.id}
                          name={widget.filterTemplate.name}
                        />

                        <Button size="icon" variant="outline" {...attributes} {...listeners}>
                          <GripVerticalIcon className="size-5" />
                        </Button>
                      </Container>
                    )}
                  </Sortable>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Container({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'hover:bg-primary-50 flex items-center gap-4 px-4 py-2 transition-colors border rounded-lg border-primary-50 bg-white',
        className,
      )}
    >
      {children}
    </div>
  )
}

function WidgetLabel({ id, name }: { id: string; name: string }) {
  const { tc } = useT()
  return (
    <Label className="flex items-center gap-1 flex-1 text-base select-none">
      {id === defaultWidget.filterTemplate.id ? (
        <>
          {tc('defaultWidgetName')}
          <Users2Icon className="size-4" />
        </>
      ) : (
        name
      )}
    </Label>
  )
}
